<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("manageMeetings") }}</h4>
        <div class="d-flex flex-row">
          <b-button variant="outline-secondary" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button class="ml-4" variant="outline-primary" to="/meetings/new" v-if="isCustomer() || isCustomerUser()">
            <b-icon icon="calendar2-plus"></b-icon> {{ $t("createMeeting") }}
          </b-button>

          <b-button class="ml-4" variant="outline-primary" @click="onClickStartNewMeeting" v-if="isCustomer() || isCustomerUser()">
            <b-icon icon="plus"></b-icon> {{ $t("startNewMeeting") }}
          </b-button>
        </div>
      </div>
      
        <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="onClickSearchBtn">
          <b-form-input style="width: 280px;" class="mr-3" type="search" minlength="2" maxlength="200" v-model="searchText" :placeholder="$t('keyword')" />
          <b-form-select style="width: 280px;" class="mr-3" :options="filterStatusOptions" v-model="filterStatus" />
          <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
            <div class="flex-shrink-0 mr-3">{{ $t("fromDate")  }}</div>
              <b-form-datepicker v-model="startDate" value-as-date required
                              :max="endDate ? endDate: new Date()"
                              no-flip hide-header
                              label-no-date-selected="DD/MM/YYYY"
                              locale="vi"
                              :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                              start-weekday="1"></b-form-datepicker>
          </div>
          <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
            <div class="flex-shrink-0 mr-3">{{ $t("toDate")  }}</div>
              <b-form-datepicker v-model="endDate" value-as-date required
                             :min= "startDate ? startDate: new Date()"
                             no-flip hide-header
                             label-no-date-selected="DD/MM/YYYY"
                             locale="vi"
                             :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                             start-weekday="1"></b-form-datepicker>
          </div>
          <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
        </b-form>

      <b-table ref="table" :items="tableItemProvider" hover show-empty primary-key="id" :fields="tableFields"
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>

        <template v-slot:cell(name)="data">
          <router-link v-if="data.item.owner || isUserAdmin() || data.item.canView" :to="`/meetings/details/${data.item.id}`">
            {{ data.item.name }}
          </router-link>
          <span v-else>{{ data.item.name }}</span>
          <br>
<!--          <span class="mr-3" v-b-tooltip title="Số người tham gia">
            <b-icon icon="people" class="mr-1"/> {{ data.item.numOfParticipants }}
          </span>-->
          <span class="mr-3" v-if="data.item.enableCamera" v-b-tooltip :title="$t('meetingShareCamera')">
            <b-icon-camera-video />
          </span>
          <span class="mr-3" v-else v-b-tooltip :title="$t('meetingTurnOffCamera')">
            <b-icon-camera-video-off />
          </span>
          <template v-if="isBBB(data.item)">
            <span class="mr-3" v-if="data.item.enableMuteOnStart" v-b-tooltip :title="$t('meetingMuteOnStart')">
              <b-icon-mic-mute />
            </span>
            <span class="mr-3" v-if="data.item.enablePrivateChat || data.item.enablePublicChat" v-b-tooltip
                  :title="getMeetingChatSettingsTooltipText(data.item)">
              <b-icon-chat-dots />
            </span>
            <span class="mr-3" v-else v-b-tooltip :title="$t('meetingChatNotAllow')">
              <b-iconstack>
                <b-icon-chat />
                <b-icon-slash rotate="90" font-scale="2" shift-h="-4" shift-v="4" />
              </b-iconstack>
            </span>
<!--            <span class="mr-3" v-if="data.item.enableSendAttachmentInChat" v-b-tooltip
                  title="Cho phép gửi tập tin trong chat">
              <b-icon-paperclip />
            </span>-->
          </template>
          <span class="mr-3" v-if="data.item.isRecorded == 1" v-b-tooltip :title="$t('meetingHasRecording')">
            <b-icon-record-circle />
          </span>
        </template>

        <template v-slot:cell(datetime)="data">
          <div :title="data.item.dateTimeTitleAttr">
            {{ data.item.dateStr }}<br>
            {{ data.item.timeStr }}<sup>{{ data.item.daysDiffStr }}</sup>
          </div>
        </template>

        <template v-slot:cell(status)="data">
          <b-badge :variant="statusBadgeVariant[data.item.status]">{{ statusText[data.item.status] }}</b-badge>
        </template>

        <template v-slot:cell(actions)="data">
          <b-button v-if="data.item.owner || isUserAdmin() || data.item.canView" variant="outline-primary" size="sm"
                    class="mr-3" :to="`/meetings/details/${data.item.id}`">
            <b-icon icon="eye" class="mr-1"></b-icon>
            {{ $t("details") }}
          </b-button>
          <template v-if="data.item.owner">
            <b-button variant="outline-primary" size="sm" class="mr-3" v-if="data.item.status === 'PENDING'"
                      @click="onClickStartMeeting(data.item)">
              <b-icon icon="display" class="mr-1"></b-icon> {{ $t("start") }}
            </b-button>

            <template v-if="data.item.status === 'INPROGRESS'">
              <b-button variant="outline-primary" size="sm" class="mr-3" @click="onClickJoinMeeting(data.item)">
                <b-icon icon="display" class="mr-1"></b-icon>
                {{ $t("joinMeeting") }}
              </b-button>
              <b-button variant="outline-danger" size="sm" class="mr-3" @click="onClickEndMeeting(data.item)">
                <b-icon icon="power" class="mr-1"></b-icon>
                {{ $t("endMeeting") }}
              </b-button>
            </template>

            <b-button variant="outline-secondary" size="sm" :to="`/meetings/new?cloneFrom=${data.item.id}`">
              <b-icon-files class="mr-1" /> {{ $t("copy") }}
            </b-button>
          </template>
          <b-button v-if="!isViewUser() && ['INPROGRESS', 'END'].includes(data.item.status)" class="mx-2" variant="link" size="sm" :to="`/monitor-meeting/${data.item.id}`"
                    v-b-tooltip.hover :title="$t('viewStats')">
            <b-icon-graph-up></b-icon-graph-up>
          </b-button>
        </template>

      </b-table>

      <!--<div class="d-flex flex-row justify-content-end">
        <b-pagination class="m-0" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
      </div>-->
      
      <div class="d-flex flex-row align-items-center" style="justify-content: flex-end;">
        <div class="d-flex flex-row align-items-center" style="width: 50%;">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

    </b-container>

  </main>
</template>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import {BTable, BvTableFieldArray} from "bootstrap-vue";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";

  import * as meetingService from "@/service/meetingService";
  import {DateTime} from "luxon";


  @Component
  export default class Meetings extends BaseComponent {

    readonly statusText = {
      PENDING : this.$t("meetingStatusPending").toString(),
      INPROGRESS : this.$t("meetingStatusInProgress").toString(),
      END : this.$t("meetingStatusEnded").toString(),
      CANCEL : this.$t("meetingStatusCancelled").toString(),
    };

    readonly statusBadgeVariant = {
      PENDING : "warning",
      INPROGRESS : "success",
      END : "secondary",
      CANCEL : "danger"
    };

    searchText = "";
    filterStatus: string = null;
    readonly filterStatusOptions = [
      { value: null, text: this.$t("all").toString() },
      { value: "PENDING", text: this.$t("meetingStatusPending").toString() },
      { value: "INPROGRESS", text: this.$t("meetingStatusInProgress").toString() },
      { value: "END", text: this.$t("meetingStatusEnded").toString() },
      { value: "CANCEL", text: this.$t("meetingStatusCancelled").toString() },
    ];

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    startDate: Date = null;
    endDate: Date = null;

    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "ID"
      },
      {
        key: "name",
        label: this.$t("meetingName").toString(),
      },
      {
        key: "createTime",
        formatter: this.formatter,
        label: this.$t("createdDate").toString(),
      },
      {
        key: "datetime",
        label: this.$t("datetime").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "status",
        label: this.$t("status").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    @Ref("table") table: BTable;


    mounted() {
      
    }

    formatter(value, key, index) {
      value = new Date(value);
      return value.toLocaleDateString('en-GB');
    }

    onClickReloadBtn() {
      this.searchText = "";
      this.filterStatus = null;
      this.currentPage = 1;

      this.table.refresh();
    }

    onClickSearchBtn() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
    
      let startDateString = "";
      if(this.startDate != null){
        const startDate = new Date(this.startDate);
        startDate.setDate(startDate.getDate() + 1);
        startDate.setUTCHours(0, 0, 0, 0);
        startDateString = startDate.toISOString();

      }

      let endDateString = "";
      if(this.endDate != null){
        const currentEndDate = new Date(this.endDate);
        currentEndDate.setDate(currentEndDate.getDate() + 2);
        currentEndDate.setUTCHours(0, 0, 0, 0);
        endDateString = currentEndDate.toISOString();
      }
      try {
        const res = await meetingService.getAllMeetings({
          q: this.searchText?.trim(),
          status: this.filterStatus,
          fromDate: this.startDate ? startDateString : null,
          toDate: this.endDate ? endDateString : null,
          page: ctx.currentPage,
          pageSize: ctx.perPage,
        });

        for (const item of res.data) {
          let startDateTime: DateTime;
          let endDateTime: DateTime;

          if (item.status == "END" || item.status == "INPROGRESS") {
            startDateTime = DateTime.fromISO(item.actualStartTime);
            endDateTime = DateTime.fromISO(item.actualEndTime);
          }
           else {
            startDateTime = DateTime.fromISO(item.scheduleStartTime);
            endDateTime = DateTime.fromISO(item.scheduleEndTime);
          }

          item.dateStr = startDateTime.toFormat("dd/MM/yyyy");
          item.timeStr = startDateTime.toFormat("HH:mm") + " - " + endDateTime.toFormat("HH:mm");
          if(item.status == "INPROGRESS" || (item.status == "END" && item.actualEndTime == null)){
            item.timeStr = startDateTime.toFormat("HH:mm");
          }

          const daysDiff = endDateTime.startOf("day").diff(startDateTime.startOf("day")).as("days");
          if (daysDiff > 0) {
            item.daysDiffStr = "+" + daysDiff;
          } else if (daysDiff < 0) {
            item.daysDiffStr = daysDiff.toString();
          }
        }

        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }


    isBBB(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "bbb";
    }

    isZoom(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "zoom";
    }


    getMeetingChatSettingsTooltipText(meeting) {
      return [
        meeting.enablePrivateChat ? this.$t("meetingPrivateChatAllow").toString() : undefined,
        meeting.enablePublicChat ? this.$t("meetingPublicChatAllow").toString() : undefined,
      ].filter(_.identity).join("\n");
    }


    async onClickStartMeeting(meeting) {
      const confirm = await this.$bvModal.msgBoxConfirm(this.$t("startMeetingWarning").toString(), {
        okVariant: 'primary',
        okTitle: this.$t("ok").toString(),
        cancelTitle: this.$t("cancel").toString(),
      });
      if (!confirm) return;

      try {
        this.showLoading();
        const res = await meetingService.startMeeting(meeting.id);
        this.table.refresh();
        open(res.startUrl);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }

    async onClickJoinMeeting(meeting) {
      try {
        this.showLoading();
        const res = await meetingService.joinMeeting(meeting.id);
        this.table.refresh();
        open(res.joinUrl);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }

    async onClickEndMeeting(meeting) {
      const modalMessage = this.$t("endMeetingWarning", { name: meeting.name, id: meeting.id }).toString();
      const confirm = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirm) {
        try {
          this.showLoading();
          const res = await meetingService.endMeeting(meeting.id);
          this.table.refresh();

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

    async onClickStartNewMeeting() {
      try {
        this.showLoading();
        const res = await meetingService.startNewMeeting();
        this.table.refresh();
        open(res.startUrl);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }


  };
</script>

